/**
 * Notice content engine — the substantive protections and state-mandated
 * content that a compliant breach notice must carry beyond the HIPAA
 * structural skeleton. Everything here is DETERMINISTIC, citated data:
 * no AI writes a word of a legal notice.
 *
 * Three layers:
 *   1. PROTECTIONS — the standard consumer-protection block (credit bureaus,
 *      free credit reports, fraud alerts vs security freezes, FTC, Medicare
 *      guidance). Stable public facts; satisfies the substance of
 *      45 CFR 164.404(c)(1)(C).
 *   2. Monitoring offer — merge-field driven section for the credit
 *      monitoring / identity protection services the plan is offering
 *      (vendor, duration, enrollment deadline, how to enroll).
 *   3. STATE_NOTICE_MODULES — per-state statutory content additions with
 *      citation and a verified-as-of date, the same trust model as the Law
 *      Library. A state WITHOUT a verified module never fails silently: the
 *      letter gets a loud review-required box instead.
 *
 * Content shape used by the docx renderer: { heading?, paragraphs: string[],
 * bullets?: string[] } — plain data, renderable anywhere.
 */

// ---------------------------------------------------------------------------
// 1. The protections block (verified stable public contact data)
// ---------------------------------------------------------------------------

export const CREDIT_BUREAUS = [
  { name: 'Equifax', phone: '1-800-685-1111', address: 'P.O. Box 740241, Atlanta, GA 30374', website: 'www.equifax.com' },
  { name: 'Experian', phone: '1-888-397-3742', address: 'P.O. Box 4500, Allen, TX 75013', website: 'www.experian.com' },
  { name: 'TransUnion', phone: '1-800-916-8800', address: 'P.O. Box 2000, Chester, PA 19016', website: 'www.transunion.com' },
];

export function protectionsContent({ medicare = true } = {}) {
  const sections = [];

  sections.push({
    heading: 'Monitor your credit reports',
    paragraphs: [
      'You may obtain a free copy of your credit report from each of the three nationwide credit reporting agencies by visiting www.annualcreditreport.com, by calling 1-877-322-8228, or by writing to Annual Credit Report Request Service, P.O. Box 105281, Atlanta, GA 30348-5281. You may also request a free credit report online each week from each agency. Review your reports carefully for accounts or inquiries you do not recognize.',
    ],
    bullets: CREDIT_BUREAUS.map((b) => `${b.name}: ${b.phone}, ${b.address}, ${b.website}`),
  });

  sections.push({
    heading: 'Consider a fraud alert or a security freeze',
    paragraphs: [
      'A fraud alert is free, lasts one year, and tells businesses to verify your identity before extending new credit. Contacting any one of the three agencies above places the alert with all three. If you become a victim of identity theft, you may place an extended fraud alert lasting seven years.',
      'A security freeze is also free under federal law and prevents new creditors from accessing your credit report entirely until you lift it. You must place or lift a freeze with each of the three agencies individually, using the contact information above. A freeze does not affect your existing accounts.',
    ],
  });

  sections.push({
    heading: 'Additional resources',
    paragraphs: [
      'You may learn more about protecting yourself against identity theft from the Federal Trade Commission (FTC) at www.identitytheft.gov or by calling 1-877-438-4338 (1-877-ID-THEFT), or by writing to Federal Trade Commission, 600 Pennsylvania Avenue NW, Washington, DC 20580. You may also file a complaint with the FTC or report suspected identity theft to law enforcement, and you have the right to obtain a copy of any police report filed about this incident.',
    ],
  });

  if (medicare) {
    sections.push({
      heading: 'Protecting your health benefits',
      paragraphs: [
        'Review the Explanation of Benefits statements you receive from your health plan and report any services you do not recognize. Do not share your Medicare or member identification number with anyone who contacts you unexpectedly. Suspected Medicare fraud can be reported by calling 1-800-MEDICARE (1-800-633-4227).',
      ],
    });
  }

  return sections;
}

// ---------------------------------------------------------------------------
// 2. Credit monitoring / identity protection offer (merge-field driven)
// ---------------------------------------------------------------------------

export const MONITORING_MERGE_FIELDS = [
  { key: 'monitorVendor', label: 'Monitoring provider (optional section)', placeholder: 'e.g. Kroll', required: false },
  { key: 'monitorProduct', label: 'Services included', placeholder: 'e.g. Credit Monitoring, Fraud Consultation, Identity Theft Restoration', required: false },
  { key: 'monitorMonths', label: 'Duration (months)', placeholder: 'e.g. 24', required: false },
  { key: 'monitorDeadline', label: 'Enrollment deadline', placeholder: 'e.g. October 16, 2026', required: false },
  { key: 'monitorUrl', label: 'Enrollment website', placeholder: 'e.g. https://enroll.kroll.com/...', required: false },
  { key: 'monitorPhone', label: 'Enrollment phone', placeholder: 'e.g. 1-855-555-0100', required: false },
  { key: 'monitorCodeNote', label: 'Activation code note', placeholder: 'e.g. Your unique activation code is included at the top of this letter', required: false },
];

export function monitoringOfferContent(merge = {}) {
  if (!merge.monitorVendor && !merge.monitorUrl && !merge.monitorPhone) return null;
  const vendor = merge.monitorVendor || 'our identity protection provider';
  const months = merge.monitorMonths ? `${merge.monitorMonths} months of ` : '';
  const product = merge.monitorProduct || 'credit monitoring and identity protection services';
  const paragraphs = [
    `To help protect your identity, we are offering ${months}${product} at no cost to you, provided through ${vendor}. These services are available regardless of whether you have seen any suspicious activity, and enrolling does not affect your credit score.`,
  ];
  const bullets = [];
  if (merge.monitorUrl) bullets.push(`Enroll online: ${merge.monitorUrl}`);
  if (merge.monitorPhone) bullets.push(`Enroll by phone: ${merge.monitorPhone}`);
  if (merge.monitorDeadline) bullets.push(`Enrollment deadline: ${merge.monitorDeadline}`);
  if (merge.monitorCodeNote) bullets.push(merge.monitorCodeNote);
  return { heading: 'Complimentary identity protection services', paragraphs, bullets };
}

// ---------------------------------------------------------------------------
// 3. State-mandated content modules (citated, verified-as-of dated)
// ---------------------------------------------------------------------------
// Each module states ONLY what that state's statute requires IN THE NOTICE
// beyond the HIPAA elements, with its citation. `satisfiedByBlock: true`
// means the statute's content demands are already met by the HIPAA elements
// plus the protections block, and the module exists to say so with the
// citation on record. Officer guidance (formatting rules, prohibitions,
// service-offer duties) goes in reviewNotes — surfaced in the UI, never
// silently applied.

export const STATE_MODULES_VERIFIED_AS_OF = '2026-07-06';

export const STATE_NOTICE_MODULES = {
  CA: {
    state_name: 'California',
    citation: 'Cal. Civ. Code §1798.29 / §1798.82',
    caFormat: true,
    additions: [],
    reviewNotes: [
      'California prescribes the notice format: it must be titled "Notice of Data Breach", use the headings "What Happened", "What Information Was Involved", "What We Are Doing", "What You Can Do", and "For More Information", and be in at least 10-point type. Selecting California applies this format to the letter automatically.',
      'If a Social Security, driver’s license, or California ID number was involved and your organization was the source of the breach, California requires an offer of appropriate identity theft prevention and mitigation services at no cost for at least 12 months (Civ. Code §1798.82(d)(2)(G)). Fill in the monitoring offer section.',
    ],
  },
  MA: {
    state_name: 'Massachusetts',
    citation: 'Mass. Gen. Laws c. 93H, §3(b)',
    additions: [
      'Massachusetts residents: you have the right to obtain a copy of any police report filed in connection with this incident. You also have the right to place a security freeze on your consumer credit report at no charge; instructions for placing a freeze with each credit reporting agency appear above.',
    ],
    reviewNotes: [
      'Massachusetts law directs that the notice to residents NOT describe the nature of the breach or the number of Massachusetts residents affected (c. 93H §3(b)). HIPAA separately requires a brief description of what happened. Counsel customarily resolves this by keeping the description brief and factual; review the "What Happened" wording for Massachusetts recipients with counsel before mailing.',
    ],
  },
  NY: {
    state_name: 'New York',
    citation: 'N.Y. Gen. Bus. Law §899-aa(7)',
    additions: [
      'New York residents: information about security breach response and identity theft prevention and protection is available from the New York Attorney General at 1-800-771-7755 (ag.ny.gov), the New York Department of State Division of Consumer Protection at 1-800-697-1220 (dos.ny.gov), and the Federal Trade Commission at 1-877-438-4338 (www.identitytheft.gov).',
    ],
    reviewNotes: [],
  },
  IL: {
    state_name: 'Illinois',
    citation: '815 ILCS 530/10(a)',
    satisfiedByBlock: true,
    additions: [],
    reviewNotes: [
      'Illinois requires the notice to include the toll-free numbers and addresses of the consumer reporting agencies and the FTC, with a statement that these sources can provide information about fraud alerts and security freezes. The protections block in this letter satisfies that requirement.',
    ],
  },
  TX: {
    state_name: 'Texas',
    citation: 'Tex. Bus. & Com. Code §521.053',
    satisfiedByBlock: true,
    additions: [],
    reviewNotes: [
      'Texas requires a description of the incident, the types of information involved, and contact information, all satisfied by the HIPAA content elements of this letter. Note the separate Texas AG filing duty (250+ residents) tracked in the Jurisdictions tab.',
    ],
  },
  FL: {
    state_name: 'Florida',
    citation: 'Fla. Stat. §501.171(4)(e)',
    satisfiedByBlock: true,
    additions: [],
    reviewNotes: [
      'Florida FIPA requires the date or date range of the breach, a description of the information involved, and contact information, all satisfied by the HIPAA content elements. Florida’s 30-day individual notice deadline is tracked by the deadline engine.',
    ],
  },
  WA: {
    state_name: 'Washington',
    citation: 'RCW 19.255.010',
    satisfiedByBlock: true,
    additions: [],
    reviewNotes: [
      'Washington requires the name and contact information of the reporting entity, the types of information involved, the time frame of exposure including the date of the breach and of its discovery, and the toll-free numbers of the credit reporting agencies. The letter’s dates section and protections block satisfy these.',
    ],
  },
};

/**
 * Resolve the state content for a set of selected states.
 * Every selected state yields either its verified module or an explicit
 * review-required entry — a state can never fall through silently.
 */
export function stateContentFor(stateCodes = []) {
  const verified = [];
  const reviewRequired = [];
  for (const raw of stateCodes) {
    const code = String(raw || '').toUpperCase();
    if (!code) continue;
    const mod = STATE_NOTICE_MODULES[code];
    if (mod) verified.push({ code, ...mod });
    else reviewRequired.push(code);
  }
  return { verified, reviewRequired };
}
